import {
  ACESFilmicToneMapping,
  FogExp2,
  PerspectiveCamera,
  SRGBColorSpace,
  Scene,
  WebGLRenderer,
} from 'three';
import { Palette } from './Palette.js';
import { PlayField } from './PlayField.js';
import { BloomPipeline } from './fx/BloomPipeline.js';

/**
 * Owns the renderer, scene, camera and post-processing chain, and is the single
 * place that disposes GPU resources.
 *
 * Three.js does not garbage-collect GPU memory: geometries, materials, textures
 * and render targets each need explicit disposal. Centralising that here — rather
 * than scattering it across the views — is what keeps a long session from leaking
 * across reloads.
 */

export interface StageOptions {
  readonly canvas: HTMLCanvasElement;
  /** Upper bound on device pixel ratio. 2 is the usual quality/cost knee. */
  readonly maxPixelRatio?: number;
  readonly fieldRadius?: number;
  readonly fogDensity?: number;
}

export interface FrameStats {
  /** Draw calls issued for the scene pass only. */
  readonly sceneCalls: number;
  /** Draw calls including every post-processing pass. */
  readonly totalCalls: number;
  readonly triangles: number;
  readonly points: number;
  readonly programs: number;
  readonly geometries: number;
  readonly textures: number;
}

export interface StageViewport {
  readonly width: number;
  readonly height: number;
  readonly bufferWidth: number;
  readonly bufferHeight: number;
  readonly pixelRatio: number;
  readonly devicePixelRatio: number;
  readonly aspect: number;
  readonly orientation: 'portrait' | 'landscape';
}

export class Stage {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly field: PlayField;
  readonly bloom: BloomPipeline;

  private sceneCalls = 0;
  private disposed = false;
  private readonly maxPixelRatio: number;

  constructor(options: StageOptions) {
    const requestedRatio = options.maxPixelRatio ?? 2;
    this.maxPixelRatio = Number.isFinite(requestedRatio) && requestedRatio > 0 ? requestedRatio : 2;

    this.renderer = new WebGLRenderer({
      canvas: options.canvas,
      antialias: false, // Bloom hides edge aliasing; MSAA would cost more than it returns.
      powerPreference: 'high-performance',
      alpha: false,
      stencil: false,
      depth: true,
    });
    this.renderer.setPixelRatio(this.pixelRatio());
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.setClearColor(Palette.space, 1);
    // Manual reset so draw calls can be counted across every composer pass rather
    // than just the last one.
    this.renderer.info.autoReset = false;

    this.scene = new Scene();
    this.scene.fog = new FogExp2(Palette.fog.getHex(), options.fogDensity ?? 0.0115);

    this.camera = new PerspectiveCamera(46, 1, 0.5, 900);
    this.field = new PlayField(options.fieldRadius ?? 9);
    this.camera.position.set(0, 0, this.field.fitDistance(this.camera));
    this.camera.lookAt(0, 0, 0);

    this.bloom = new BloomPipeline(this.renderer, this.scene, this.camera);

    this.resize();
  }

  /** Matches drawing buffer and camera to the canvas' CSS size. */
  resize(): void {
    const canvas = this.renderer.domElement;
    const width = Math.max(1, canvas.clientWidth);
    const height = Math.max(1, canvas.clientHeight);

    const pixelRatio = this.pixelRatio();
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(width, height, false);
    this.resizeCamera(width, height);
    this.bloom.resize(width, height, pixelRatio);
  }

  /** CSS coordinates drive input; drawing-buffer coordinates describe GPU cost. */
  get viewport(): StageViewport {
    const canvas = this.renderer.domElement;
    const width = Math.max(1, canvas.clientWidth);
    const height = Math.max(1, canvas.clientHeight);
    return {
      width,
      height,
      bufferWidth: canvas.width,
      bufferHeight: canvas.height,
      pixelRatio: this.renderer.getPixelRatio(),
      devicePixelRatio: this.devicePixelRatio(),
      aspect: this.camera.aspect,
      orientation: width < height ? 'portrait' : 'landscape',
    };
  }

  private devicePixelRatio(): number {
    const ratio = window.devicePixelRatio;
    return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  }

  private pixelRatio(): number {
    return Math.min(this.devicePixelRatio(), this.maxPixelRatio);
  }

  private resizeCamera(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    // Preserve the director's angle and roll, but fit immediately. Input can
    // arrive between a resize and the next animation frame.
    if (this.camera.position.lengthSq() === 0) this.camera.position.z = this.framingDistance;
    else this.camera.position.setLength(this.framingDistance);
    this.camera.updateMatrixWorld(true);
  }

  /**
   * Resizes the post chain to an explicit size.
   *
   * Separate from `resize` because that one derives its size from the canvas'
   * CSS box, which is the right source while the game is on screen and the
   * wrong one for an offline render — a 4K export must not depend on how large
   * the browser window happens to be. The bloom targets have to follow the
   * drawing buffer or the composer samples a texture of the wrong size.
   */
  resizeTargets(width: number, height: number): void {
    const safeWidth = Math.max(1, Math.floor(width));
    const safeHeight = Math.max(1, Math.floor(height));
    this.resizeCamera(safeWidth, safeHeight);
    this.bloom.resize(safeWidth, safeHeight, this.renderer.getPixelRatio());
  }

  /** The camera distance that frames the play field at the current aspect. */
  get framingDistance(): number {
    return this.field.fitDistance(this.camera);
  }

  render(): void {
    this.renderer.info.reset();
    this.bloom.render();
    this.sceneCalls = this.bloom.sceneCalls;
  }

  stats(): FrameStats {
    const info = this.renderer.info;
    return {
      sceneCalls: this.sceneCalls,
      totalCalls: info.render.calls,
      triangles: info.render.triangles,
      points: info.render.points,
      programs: info.programs?.length ?? 0,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.bloom.dispose();
    this.scene.traverse((object) => {
      const holder = object as {
        geometry?: { dispose(): void };
        material?: { dispose(): void } | { dispose(): void }[];
      };
      holder.geometry?.dispose();
      const material = holder.material;
      if (Array.isArray(material)) material.forEach((entry) => entry.dispose());
      else material?.dispose();
    });
    this.scene.clear();
    this.renderer.dispose();
  }
}
